// app/javascript/components/PersonnelViewModal.jsx
import React, { useEffect, useRef } from 'react';

const isImage = (b64) => {
  if (!b64 || typeof b64 !== 'string') return false;
  const c = b64.replace(/\s+/g, '');
  return (
    c.startsWith('data:image/') ||
    c.startsWith('/9j/') ||
    c.startsWith('iVBOR') ||
    c.startsWith('Qk')
  );
};

const toDataUrl = (b64) => {
  if (!b64) return '';
  const c = b64.replace(/\s+/g, '');
  if (c.startsWith('data:')) return c;
  if (c.startsWith('/9j/')) return `data:image/jpeg;base64,${c}`;
  if (c.startsWith('iVBOR')) return `data:image/png;base64,${c}`;
  if (c.startsWith('Qk')) return `data:image/bmp;base64,${c}`;
  return `data:image/jpeg;base64,${c}`;
};

const Row = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>
      <span style={{ minWidth: '180px', fontWeight: 600, color: '#555', fontSize: '13px' }}>{label}:</span>
      <span style={{ color: '#222', fontSize: '13px', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

const PersonnelViewModal = ({ isOpen, onClose, user, api }) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !user) return;
    setDetail(null);

    // List endpoint no longer includes army_photo/biometric_data (removed for performance)
    // Always fetch full record for the modal
    const ap = user.army_personnel;
    if (!api || !ap?.id) {
      setDetail(user);
      return;
    }

    setLoading(true);
    api.get(`/army_personnel/${ap.id}?user_id=${user.id}`)
      .then(res => {
        const fullAp = res?.data?.army_personnel || res?.data || res;
        setDetail({ ...user, army_personnel: { ...ap, ...fullAp } });
      })
      .catch(() => setDetail(user))
      .finally(() => setLoading(false));
  }, [isOpen, user]);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Personnel Information</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .row { display: flex; gap: 8px; padding: 4px 0; border-bottom: 1px solid #eee; }
        .label { min-width: 160px; font-weight: bold; color: #555; }
        img { max-width: 180px; height: 180px; object-fit: cover; border: 1px solid #ccc; border-radius: 4px; }
        .sig-img { max-width: 220px; height: 80px; object-fit: contain; }
        .fp-img { width: 80px; height: 100px; object-fit: contain; border: 1px solid #ccc; }
        h2 { margin: 0 0 12px; font-size: 16px; }
        .section { margin-bottom: 16px; }
        @media print { button { display: none; } }
      </style></head><body>
      ${content.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  if (!isOpen) return null;

  const ap = detail?.army_personnel || {};
  const email = detail?.email || '';

  // Debug
  if (process.env.NODE_ENV !== 'production') {
    console.log('[ViewModal] army_photo present:', !!ap.army_photo, 'length:', ap.army_photo?.length, 'prefix:', ap.army_photo?.substring(0, 40));
    console.log('[ViewModal] biometric_data:', ap.biometric_data?.length, 'types:', ap.biometric_data?.map(b => b.biometric_type));
  }

  const photo = ap.army_photo;
  const signature = ap.biometric_data?.find(b =>
    b.biometric_type === 'signature' || b.biometric_type === 1 || b.biometric_type === '1'
  )?.data;
  const fingerprints = (ap.biometric_data || []).filter(b =>
    b.biometric_type === 'fingerprint' || b.biometric_type === 0 || b.biometric_type === '0'
  ).filter(b => !b.raw_template?.includes('DEVPIN:'));

  const fmt = (val) => val || 'N/A';
  const fmtDate = (val) => {
    if (!val) return 'N/A';
    try { return new Date(val).toLocaleDateString(); } catch { return val; }
  };
  const activeLabel = ap.active_status == 1 ? 'Active' : ap.active_status == 2 ? 'Rejected' : 'Pending';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        zIndex: 9999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: '10px', width: '100%',
        maxWidth: '820px', maxHeight: '92vh', display: 'flex',
        flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #eee',
        }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: '16px' }}>Personnel Information</h5>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handlePrint}
              disabled={loading}
            >
              🖨 Print
            </button>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              Loading details...
            </div>
          )}
          {!loading && (
            <div ref={printRef}>
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '24px' }}>
                {/* Left column — photo / signature / fingerprints */}
                <div>
                  {/* Photo */}
                  <div style={{ marginBottom: '16px' }}>
                    {photo && photo.length > 50 ? (
                      <img
                        src={toDataUrl(photo)}
                        alt="Profile"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        style={{
                          width: '180px', height: '180px', objectFit: 'cover',
                          borderRadius: '6px', border: '1px solid #ddd', display: 'block',
                        }}
                      />
                    ) : null}
                    <div style={{
                      width: '180px', height: '180px', background: '#f0f0f0',
                      borderRadius: '6px', display: photo && photo.length > 50 ? 'none' : 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: '#aaa', fontSize: '13px', border: '1px solid #ddd',
                    }}>
                      No Photo
                    </div>
                  </div>

                  {/* Signature */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 600, fontSize: '12px', color: '#555', marginBottom: '6px' }}>
                      Signature:
                    </div>
                    {signature && signature.length > 50 ? (
                      <img
                        src={toDataUrl(signature)}
                        alt="Signature"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        style={{
                          maxWidth: '180px', height: '70px', objectFit: 'contain',
                          border: '1px solid #ddd', borderRadius: '4px', display: 'block',
                        }}
                      />
                    ) : null}
                    <div style={{
                      width: '180px', height: '70px', background: '#f9f9f9',
                      border: '1px solid #ddd', borderRadius: '4px',
                      display: signature && signature.length > 50 ? 'none' : 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: '#bbb', fontSize: '12px',
                    }}>
                      No Signature
                    </div>
                  </div>

                  {/* Fingerprints */}
                  {fingerprints.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '12px', color: '#555', marginBottom: '6px' }}>
                        Fingerprints:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {fingerprints.map((fp, i) => {
                          const imgData = fp.data;
                          return isImage(imgData) ? (
                            <img
                              key={i}
                              src={toDataUrl(imgData)}
                              alt={`Finger ${i + 1}`}
                              title={`Finger ${i + 1}`}
                              style={{
                                width: '70px', height: '90px', objectFit: 'contain',
                                border: '1px solid #ddd', borderRadius: '4px',
                                background: '#f5f5f5',
                              }}
                            />
                          ) : (
                            <div key={i} style={{
                              width: '70px', height: '90px', background: '#f5f5f5',
                              border: '1px solid #ddd', borderRadius: '4px',
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: '10px', color: '#2e7d32', fontWeight: 600, gap: '4px',
                            }}>
                              <span style={{ fontSize: '20px' }}>🖐</span>
                              Enrolled
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right column — details */}
                <div>
                  <Row label="Full Name"               value={fmt(ap.full_name)} />
                  <Row label="Email"                   value={fmt(email)} />
                  <Row label="Mother's Name"           value={fmt(ap.mother_name)} />
                  <Row label="Phone"                   value={fmt(ap.phone)} />
                  <Row label="Place of Birth"          value={fmt(ap.place_of_birth)} />
                  <Row label="Birth Date"              value={fmtDate(ap.birth_date)} />
                  <Row label="Blood Type"              value={fmt(ap.blood_type)} />
                  <Row label="Gender"                  value={fmt(ap.gender)} />
                  <Row label="Marital Status"          value={fmt(ap.marital_status)} />
                  <Row label="Educational Level"       value={fmt(ap.educational_level)} />
                  <Row label="Army Type"               value={fmt(ap.army_type?.name)} />
                  <Row label="Army Department"         value={fmt(ap.department?.name)} />
                  <Row label="Sub Department"          value={fmt(ap.sub_department?.name)} />
                  <Row label="Rank"                    value={fmt(ap.rank)} />
                  <Row label="Title"                   value={fmt(ap.title)} />
                  <Row label="Organization"            value={fmt(ap.organization)} />
                  <Row label="Work Place"              value={fmt(ap.work_place)} />
                  <Row label="Status"                  value={fmt(ap.status)} />
                  <Row label="Active Status"           value={activeLabel} />
                  <Row label="Address"                 value={fmt(ap.address)} />
                  <Row label="Bank Name"               value={fmt(ap.bank_name)} />
                  <Row label="Bank Account"            value={fmt(ap.bank_account)} />
                  <Row label="Salary (USD)"            value={ap.salary_usd ? `$${ap.salary_usd} (Payroll Controlled)` : 'Payroll Controlled'} />
                  <Row label="Allowance (USD)"         value={ap.allowance_usd ? `$${ap.allowance_usd} (Payroll Controlled)` : 'Payroll Controlled'} />
                  <Row label="Emergency Contact"       value={fmt(ap.emergency_contact)} />
                  <Row label="Emergency Contact Phone" value={fmt(ap.emergency_contact_phone)} />
                  <Row label="Emergency Relation"      value={fmt(ap.emergency_contact_relation)} />
                  <Row label="Responsible Person"      value={fmt(ap.responsible_person_name)} />
                  <Row label="Responsible Phone"       value={fmt(ap.responsible_person_phone)} />
                  <Row label="Training"                value={fmt(ap.training)} />
                  <Row label="Army Equipment"          value={fmt(ap.army_equipment)} />
                  <Row label="Remarks"                 value={fmt(ap.remarks)} />
                  <Row label="Registered Location"     value={[ap.location?.district, ap.location?.region, ap.location?.station].filter(Boolean).join(', ') || 'N/A'} />
                  <Row label="Created Date"            value={fmtDate(ap.created_date)} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #eee',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PersonnelViewModal;
